import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar, Users, Search, Download, QrCode, Mail, Phone, Trash2, MapPin, Armchair, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { AdminSeatBlockingDialog } from './AdminSeatBlockingDialog';

interface EventRegistration {
  id: string;
  event_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  registration_code: string;
  qr_code_url: string | null;
  status: string;
  created_at: string;
  event_title?: string;
  event_date?: string;
  section?: string | null;
  row_number?: string | null;
  seat_number?: string | null;
}

export const EventRegistrationsSection = () => {
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<string>('all');
  const [events, setEvents] = useState<Array<{id: string, title: string}>>([]);
  const [regeneratingQR, setRegeneratingQR] = useState<string | null>(null);
  const [venueLayout, setVenueLayout] = useState<any>(null);
  const [showSeatBlocking, setShowSeatBlocking] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchRegistrations();
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('news')
        .select('id, title')
        .eq('news_type', 'event')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (error: any) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      
      // First fetch registrations
      const { data: regData, error: regError } = await supabase
        .from('event_registrations')
        .select('*')
        .order('created_at', { ascending: false });

      if (regError) throw regError;

      // Then fetch event details for each registration
      const registrationsWithEvents = [];
      for (const reg of regData || []) {
        const { data: eventData, error: eventError } = await supabase
          .from('news')
          .select('title, event_date')
          .eq('id', reg.event_id)
          .single();

        registrationsWithEvents.push({
          ...reg,
          event_title: eventData?.title || 'N/A',
          event_date: eventData?.event_date
        });
      }

      setRegistrations(registrationsWithEvents);
    } catch (error: any) {
      console.error('Error fetching registrations:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել գրանցումները",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteRegistration = async (id: string) => {
    if (!confirm('Ցանկանում եք ջնջել այս գրանցումը?')) return;

    try {
      const { error } = await supabase
        .from('event_registrations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setRegistrations(prev => prev.filter(reg => reg.id !== id));
      toast({
        title: "Հաջողություն",
        description: "Գրանցումը ջնջվել է",
      });
    } catch (error: any) {
      console.error('Error deleting registration:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ջնջել գրանցումը",
        variant: "destructive",
      });
    }
  };

  const regenerateQRCode = async (registrationId: string, registrationCode: string) => {
    try {
      setRegeneratingQR(registrationId);
      
      // Get full registration data
      const registration = registrations.find(r => r.id === registrationId);
      if (!registration) throw new Error('Registration not found');
      
      // Get event date
      const { data: eventData } = await supabase
        .from('news')
        .select('event_date')
        .eq('id', registration.event_id)
        .single();
      
      // Create JSON payload with full registration info
      const qrPayload = {
        registrationCode: registrationCode,
        fullName: `${registration.first_name} ${registration.last_name}`,
        email: registration.email,
        phone: registration.phone,
        eventTitle: registration.event_title || 'Միջոցառում',
        eventDate: eventData?.event_date || new Date().toLocaleDateString('hy-AM'),
        section: registration.section || '',
        row: registration.row_number || '',
        seat: registration.seat_number || '',
        registrationDate: new Date(registration.created_at).toLocaleDateString('hy-AM')
      };
      
      // Generate QR code with full data (larger size for more data)
      const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(JSON.stringify(qrPayload))}`;
      
      // Update registration with new QR code URL
      const { error } = await supabase
        .from('event_registrations')
        .update({ qr_code_url: qrApiUrl })
        .eq('id', registrationId);

      if (error) throw error;

      // Update local state
      setRegistrations(prev => 
        prev.map(reg => 
          reg.id === registrationId 
            ? { ...reg, qr_code_url: qrApiUrl }
            : reg
        )
      );

      toast({
        title: "Հաջողություն",
        description: "QR կոդը հաջողությամբ գեներացվել է ամբողջ տեղեկատվությամբ",
      });
    } catch (error: any) {
      console.error('Error regenerating QR code:', error);
      toast({
        title: "Սխալ",
        description: "QR կոդ գեներացնելիս սխալ տեղի ունեցավ",
        variant: "destructive",
      });
    } finally {
      setRegeneratingQR(null);
    }
  };

  const downloadTicket = async (registration: EventRegistration) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      canvas.width = 800;
      canvas.height = 1000;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 2;
      ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);

      ctx.fillStyle = '#059669';
      ctx.fillRect(40, 40, canvas.width - 80, 80);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 28px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('ՄԻՋՈՑԱՌՄԱՆ ՏՈՄՍ', canvas.width / 2, 95);

      ctx.fillStyle = '#1f2937';
      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'center';
      const eventTitle = registration.event_title || 'Միջոցառում';
      const maxWidth = canvas.width - 100;
      const words = eventTitle.split(' ');
      let line = '';
      let y = 180;
      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;
        if (testWidth > maxWidth && n > 0) {
          ctx.fillText(line, canvas.width / 2, y);
          line = words[n] + ' ';
          y += 30;
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line, canvas.width / 2, y);

      y += 80;
      ctx.font = '20px Arial';
      ctx.textAlign = 'left';
      ctx.fillStyle = '#374151';
      const infoData: [string, string][] = [
        ['Անուն:', `${registration.first_name} ${registration.last_name}`],
        ['Էլ. փոստ:', registration.email],
        ['Հեռախոս:', registration.phone],
        ['Գրանցման կոդ:', registration.registration_code],
        ['Ամսաթիվ:', new Date(registration.created_at).toLocaleDateString('hy-AM')]
      ];
      
      if (registration.row_number && registration.seat_number) {
        infoData.push(['Շարք:', registration.row_number]);
        infoData.push(['Տեղ:', registration.seat_number]);
      }
      
      infoData.forEach(([label, value]) => {
        ctx.font = 'bold 18px Arial';
        ctx.fillText(label, 80, y);
        ctx.font = '18px Arial';
        ctx.fillText(value, 250, y);
        y += 35;
      });

      y += 40;
      ctx.fillStyle = '#059669';
      ctx.font = 'bold 20px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('QR ԿՈԴ', canvas.width / 2, y);

      if (registration.qr_code_url) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise<void>((resolve) => {
          img.onload = () => {
            const qrSize = 250;
            const qrX = (canvas.width - qrSize) / 2;
            ctx.drawImage(img, qrX, y + 20, qrSize, qrSize);

            ctx.fillStyle = '#6b7280';
            ctx.font = '16px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('Այս տոմսը ներկայացրեք միջոցառման մուտքի ժամանակ', canvas.width / 2, y + 300);
            ctx.fillText('Global IT Academy', canvas.width / 2, y + 330);
            resolve();
          };
          img.onerror = () => resolve();
          img.src = registration.qr_code_url;
        });
      }

      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `ticket-${registration.registration_code}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Հաջողություն",
        description: "Տոմսը հաջողությամբ ներբեռնվել է",
      });
    } catch (error: any) {
      console.error('Error downloading ticket:', error);
      toast({
        title: "Սխալ",
        description: "Տոմս ներբեռնելիս սխալ տեղի ունեցավ",
        variant: "destructive",
      });
    }
  };

  const exportToCSV = () => {
    const csvData = filteredRegistrations.map(reg => ({
      'Անուն': reg.first_name,
      'Ազգանուն': reg.last_name,
      'Էլ. փոստ': reg.email,
      'Հեռախոս': reg.phone,
      'Կոդ': reg.registration_code,
      'Միջոցառում': reg.event_title || 'N/A',
      'Բաժին': reg.section || '-',
      'Տող': reg.row_number || '-',
      'Նստատեղ': reg.seat_number || '-',
      'Ամսաթիվ': new Date(reg.created_at).toLocaleDateString('hy-AM'),
      'Կարգավիճակ': reg.status
    }));

    if (csvData.length === 0) {
      toast({
        title: "Ծանուցում",
        description: "Արտահանելու համար տվյալներ չկան",
        variant: "destructive",
      });
      return;
    }

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).map(value => `"${value}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `event-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const filteredRegistrations = registrations.filter(reg => {
    const matchesSearch = 
      reg.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.registration_code.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesEvent = selectedEvent === 'all' || reg.event_id === selectedEvent;
    
    return matchesSearch && matchesEvent;
  });

  const totalRegistrations = registrations.length;
  const registrationsWithSeats = registrations.filter(reg => reg.seat_number).length;
  const recentRegistrations = registrations.filter(reg => {
    const regDate = new Date(reg.created_at);
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    return regDate >= oneDayAgo;
  }).length;

  if (loading) {
    return <div className="p-6">Բեռնում...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ընդամենը գրանցումներ</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalRegistrations}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Վերջին 24 ժամ</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{recentRegistrations}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Նստատեղերով գրանցումներ</CardTitle>
            <Armchair className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{registrationsWithSeats}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {totalRegistrations > 0 
                ? `${((registrationsWithSeats / totalRegistrations) * 100).toFixed(0)}% ընտրել են նստատեղ`
                : 'Դեռ գրանցումներ չկան'
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Միջոցառումների գրանցումներ</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Որոնել գրանցումները..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            
            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="px-3 py-2 border rounded-md bg-background"
            >
              <option value="all">Բոլոր միջոցառումները</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>{event.title}</option>
              ))}
            </select>

            <Button onClick={exportToCSV} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Արտահանել CSV
            </Button>

            {selectedEvent !== 'all' && (
              <Button 
                onClick={async () => {
                  // Load venue layout
                  const { data } = await supabase
                    .from('news')
                    .select('venue_layout')
                    .eq('id', selectedEvent)
                    .single();
                  
                  if (data?.venue_layout) {
                    setVenueLayout(data.venue_layout);
                    setShowSeatBlocking(true);
                  } else {
                    toast({
                      title: "Ծանուցում",
                      description: "Այս միջոցառումը չունի տեղերի դասավորություն",
                      variant: "destructive",
                    });
                  }
                }}
                variant="outline"
              >
                <Lock className="h-4 w-4 mr-2" />
                Տեղերի կառավարում
              </Button>
            )}
          </div>

          {/* Registrations Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Անուն</TableHead>
                  <TableHead>Ազգանուն</TableHead>
                  <TableHead>Էլ. փոստ</TableHead>
                  <TableHead>Հեռախոս</TableHead>
                  <TableHead>Միջոցառում</TableHead>
                  <TableHead>Նստատեղ</TableHead>
                  <TableHead>Կոդ</TableHead>
                  <TableHead>Ամսաթիվ</TableHead>
                  <TableHead>Գործողություններ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRegistrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-4">
                      Գրանցումներ չեն գտնվել
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRegistrations.map((registration) => (
                    <TableRow key={registration.id}>
                      <TableCell className="font-medium">{registration.first_name}</TableCell>
                      <TableCell>{registration.last_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <a href={`mailto:${registration.email}`} className="text-blue-600 hover:underline">
                            {registration.email}
                          </a>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <a href={`tel:${registration.phone}`} className="text-blue-600 hover:underline">
                            {registration.phone}
                          </a>
                        </div>
                      </TableCell>
                      <TableCell>{registration.event_title || 'N/A'}</TableCell>
                      <TableCell>
                        {registration.seat_number ? (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <div className="text-sm">
                              <div className="font-medium">{registration.section}</div>
                              <div className="text-muted-foreground">
                                Տող {registration.row_number}, Նստատեղ {registration.seat_number}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <Badge variant="secondary">Ազատ ընտրություն</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          {registration.registration_code}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(registration.created_at).toLocaleDateString('hy-AM')}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button size="sm" variant="outline" title="Դիտել/Գեներացնել QR կոդ">
                                <QrCode className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>QR կոդ - {registration.registration_code}</DialogTitle>
                              </DialogHeader>
                              <div className="text-center py-4">
                                {registration.qr_code_url ? (
                                  <div>
                                    <img 
                                      src={registration.qr_code_url} 
                                      alt="QR Code" 
                                      className="mx-auto mb-4 border rounded"
                                    />
                                    <p className="text-sm text-muted-foreground mb-4">
                                      {registration.first_name} {registration.last_name}
                                    </p>
                                    <div className="flex gap-2 justify-center">
                                      <Button 
                                        size="sm" 
                                        variant="outline" 
                                        onClick={() => regenerateQRCode(registration.id, registration.registration_code)}
                                        disabled={regeneratingQR === registration.id}
                                      >
                                        {regeneratingQR === registration.id ? 'Գեներացվում է...' : 'Վերագեներացնել'}
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        onClick={() => downloadTicket(registration)}
                                      >
                                        Ներբեռնել տոմս
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <div className="w-32 h-32 bg-muted border-2 border-dashed border-border rounded mx-auto mb-4 flex items-center justify-center">
                                      <QrCode className="h-8 w-8 text-muted-foreground" />
                                    </div>
                                    <p className="text-sm text-muted-foreground mb-4">
                                      QR կոդ գեներացված չէ
                                    </p>
                                    <div className="flex gap-2 justify-center">
                                      <Button 
                                        size="sm" 
                                        onClick={() => regenerateQRCode(registration.id, registration.registration_code)}
                                        disabled={regeneratingQR === registration.id}
                                      >
                                        {regeneratingQR === registration.id ? 'Գեներացվում է...' : 'Գեներացնել QR կոդ'}
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        onClick={() => downloadTicket(registration)}
                                      >
                                        Ներբեռնել տոմս
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                <div className="mt-4 p-3 bg-muted/50 rounded text-xs">
                                  <strong>Գրանցման կոդ:</strong> {registration.registration_code}
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => deleteRegistration(registration.id)}
                            title="Ջնջել գրանցումը"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Seat blocking dialog */}
      {selectedEvent !== 'all' && venueLayout && (
        <AdminSeatBlockingDialog
          eventId={selectedEvent}
          eventTitle={events.find(e => e.id === selectedEvent)?.title || ''}
          venueLayout={venueLayout}
          open={showSeatBlocking}
          onOpenChange={(open) => {
            setShowSeatBlocking(open);
            if (!open) {
              setVenueLayout(null);
            }
          }}
        />
      )}
    </div>
  );
};